<template>
  <div :class="['pix-payment', `pix-payment--${context}`]">
    <header class="pix-payment__header">
      <img
        class="pix-payment__logo"
        src="../assets/icons/pix-logo.svg"
        alt="Pix"
      />
      <div class="pix-payment__header-text">
        <h3 class="pix-payment__title">Pagamento via Pix</h3>
        <p class="pix-payment__lead">
          Escaneie o QR Code ou copie o código para concluir o pagamento.
        </p>
      </div>
      <button
        v-if="context === 'modal'"
        type="button"
        class="pix-payment__close"
        aria-label="Fechar"
        @click="$emit('close')"
      >
        &times;
      </button>
    </header>

    <div class="pix-payment__qr-card">
      <img class="pix-payment__qr" :src="resolvedQrCode" alt="QR Code Pix" />
    </div>

    <p v-if="formattedExpiresAt" class="pix-payment__expires">
      <span class="pix-payment__expires-label">Válido até</span>
      {{ formattedExpiresAt }}
    </p>

    <ol class="pix-payment__steps">
      <li>Abra o app do seu banco</li>
      <li>
        Escolha
        <strong>Pix</strong>
        e pague com QR Code ou Copia e Cola
      </li>
      <li>
        Aguarde a confirmação — sua consulta será liberada automaticamente
      </li>
    </ol>

    <div class="pix-payment__actions">
      <BaseButton
        size="xl"
        block
        v-clipboard:copy="resolvedCopyAndPaste"
        v-clipboard:success="onCopySuccess"
      >
        {{ copied ? "Código copiado!" : "Copiar código Pix" }}
      </BaseButton>

      <BaseButton
        v-if="context === 'page'"
        variant="card"
        size="xl"
        block
        @click="$router.push({ name: 'Dashboard' })"
      >
        Minhas consultas
      </BaseButton>

      <BaseButton v-else variant="card" size="xl" block
@click="$emit('close')"
>
        Fechar
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

export default {
  name: "Pix",
  props: {
    pixData: {
      type: Object,
      default: null
    },
    qrCode: {
      type: String,
      default: ""
    },
    copyAndPaste: {
      type: String,
      default: ""
    },
    context: {
      type: String,
      default: "page",
      validator: value => ["page", "modal"].includes(value)
    }
  },
  data() {
    return {
      copied: false
    };
  },
  beforeDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  },
  methods: {
    onCopySuccess() {
      this.copied = true;

      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }

      this.copyTimeout = setTimeout(() => {
        this.copied = false;
      }, 2500);
    }
  },
  computed: {
    resolvedQrCode() {
      if (this.pixData) {
        return this.pixData.qrcode || this.pixData.image || "";
      }

      return this.qrCode;
    },
    resolvedCopyAndPaste() {
      if (this.pixData) {
        return this.pixData.qrcode_text || this.pixData.emv || "";
      }

      return this.copyAndPaste;
    },
    formattedExpiresAt() {
      const expiresAt = this.pixData && this.pixData.expires_at;
      if (!expiresAt) return null;

      const date =
        typeof expiresAt === "string" ? parseISO(expiresAt) : expiresAt;
      if (!isValid(date)) return null;

      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    }
  }
};
</script>

<style lang="scss" scoped>
.pix-payment {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 1.5rem;
  background: #fff;
  border-radius: 16px;
}

.pix-payment--modal {
  padding: 1.25rem 1.5rem 1.5rem;
}

.pix-payment__header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  margin-bottom: 1.25rem;
  margin-top: 1rem;
  position: relative;
}

.pix-payment__logo {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.pix-payment__header-text {
  flex: 1;
  padding-right: 1.5rem;
}

.pix-payment__title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: #212529;
  line-height: 1.3;
}

.pix-payment__lead {
  margin: 0;
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.45;
}

.pix-payment__close {
  position: absolute;
  top: -0.25rem;
  right: 0;
  border: 0;
  background: transparent;
  color: #868e96;
  font-size: 1.75rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: #495057;
  }
}

.pix-payment__qr-card {
  display: flex;
  justify-content: center;
  padding: 1.25rem;
  margin-bottom: 1rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
}

.pix-payment__qr {
  width: 320px;
  height: 320px;
  object-fit: contain;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.pix-payment__expires {
  margin: 0 0 1rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #495057;
}

.pix-payment__expires-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #868e96;
  margin-bottom: 0.125rem;
}

.pix-payment__steps {
  margin: 0 0 1.25rem;
  padding-left: 1.125rem;
  font-size: 0.8125rem;
  color: #495057;
  line-height: 1.5;

  li + li {
    margin-top: 0.375rem;
  }
}

.pix-payment__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
